import { useDifficulties } from "~/hooks/useDifficulties"
import { IssueProjectPicker } from "~/components/issueDetailsPage/IssueProjectPicker"
import { IssuePropertiesFields } from "~/components/issueDetailsPage/IssuePropertiesFields"
import { Input } from "~/components/ui/input"

import { VisuallyHidden } from "@radix-ui/react-visually-hidden"
import {
	CircleHelpIcon,
	PaperclipIcon,
	PlusIcon,
	UserCircleIcon,
	XIcon
} from "lucide-react"
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger
} from "~/components/ui/dialog"
import { useOrganization } from "~/hooks/useOrganization"
import { usePermission } from "~/hooks/usePermission"
import { useTeam } from "~/hooks/useTeam"
import {
	ContentTemplateKind,
	IssueDifficulty,
	IssuePriority,
	IssueStatus
} from "~/lib/enums"
import {
	appShortcuts,
	firstShortcutKey,
	isModifiedShortcut,
	quickNavigationShortcuts
} from "~/lib/keyboard-shortcuts"
import { TeamPermissions } from "~/lib/permissions"
import { normalizeIssueDescription } from "~/lib/utils"
import { api, type RouterInputs } from "~/trpc/react"
import { Editor } from "../editor/Editor"
import { TemplatePicker } from "../editor/TemplatePicker"

import { IssueLabelPicker } from "../issueDetailsPage/IssueLabelPicker"
import { IssueLabelShowcase } from "../issueDetailsPage/IssueLabelShowcase"
import {
	IssueSetting,
	issueSettingAssignees
} from "../issueDetailsPage/IssueSetting"
import { RefreshButton } from "../issuePage/RefreshButton"
import { EntityIdentity } from "~/components/EntityIdentity"
import { Button } from "../ui/button"

export function IssueCreateDialog({
	presetTeamId,
	presetProjectId,
	presetValues,
	open: controlledOpen,
	onOpenChange,
	children
}: {
	presetTeamId?: string | null
	presetProjectId?: string
	presetValues?: Partial<
		Pick<
			RouterInputs["issues"]["create"],
			"status" | "priority" | "difficulty" | "assigneeId"
		>
	>
	open?: boolean
	onOpenChange?: (open: boolean) => void
	children: ReactNode
}) {
	const [internalOpen, setInternalOpen] = useState(false)
	const open = controlledOpen ?? internalOpen
	const setOpen = onOpenChange ?? setInternalOpen
	const [issueState, setIssueState] = useState<{
		title: string
		description: string | null
		status: IssueStatus
		priority: IssuePriority
		difficulty: IssueDifficulty
		dueDate: Date | null
		assigneeId: string | null
		projectId: string | null
		labelIds: string[]
	}>({
		title: "",
		description: null,
		status: IssueStatus.ToDo,
		priority: IssuePriority.None,
		difficulty: null,
		dueDate: null,
		assigneeId: null,
		projectId: presetProjectId ?? null,
		labelIds: []
	})
	const [teamId, setTeamId] = useState<string | null>(presetTeamId ?? null)
	const difficulties = useDifficulties(teamId)
	const modifierKeyDown = useRef(false)

	const { organization } = useOrganization()
	const { team } = useTeam()
	const { hasTeamPermission } = usePermission()
	const canQueue = hasTeamPermission(TeamPermissions.CREATE_QUEUE_ISSUES)
	const canCreate = hasTeamPermission(TeamPermissions.CREATE_ISSUES)
	const enabled = team
		? team.enableIssues && (canQueue || canCreate)
		: !!organization

	const { data: teamData } = api.team.get.useQuery(
		{ teamId: teamId ?? "" },
		{
			enabled: !!teamId
		}
	)

	const { data: allTeams, refetch: refetchAllTeams } =
		api.user.getTeams.useQuery(
			{
				organizationId: organization?.id ?? "",
				teamPermissionFilter: [
					TeamPermissions.CREATE_QUEUE_ISSUES,
					TeamPermissions.CREATE_ISSUES
				]
			},
			{
				enabled: !!organization?.id,
				select: (teams) => teams.filter((team) => team.enableIssues)
			}
		)

	const { data: teamLabels } = api.labels.list.useQuery(
		{ teamId: teamId ?? "" },
		{ enabled: !!teamId }
	)

	const navigate = useNavigate()
	const utils = api.useUtils()
	const createIssue = api.issues.create.useMutation({
		onError: (error) => toast.error(error.message),
		onSuccess: (data) => {
			utils.issues.getTeamIssues.invalidate()
			utils.user.getTeams.invalidate()
			void utils.projects.list.invalidate()
			setOpen(false)
			setIssueState({
				title: "",
				description: null,
				status: IssueStatus.ToDo,
				priority: IssuePriority.None,
				difficulty: null,
				dueDate: null,
				assigneeId: null,
				projectId: null,
				labelIds: []
			})
			navigate(`/${organization?.slug}/${teamData?.slug}/${data.friendlyId}`)
		}
	})
	const canSubmitIssue =
		!!teamData?.id &&
		!createIssue.isPending &&
		!!issueState.title.trim() &&
		(canQueue || canCreate)
	const submitIssue = useCallback(() => {
		if (!teamData?.id) {
			toast.error("Select a team before creating an issue")
			return
		}
		if (!issueState.title.trim()) {
			toast.error("Give the issue a title before submitting")
			return
		}
		if (createIssue.isPending || (!canQueue && !canCreate)) {
			return
		}

		createIssue.mutate({
			...issueState,
			title: issueState.title,
			description: normalizeIssueDescription(issueState.description),
			teamId: teamData.id,
			projectId: teamData.enableProjects ? issueState.projectId : null
		})
	}, [
		canCreate,
		canQueue,
		createIssue,
		issueState,
		teamData?.id,
		teamData?.enableProjects
	])

	useEffect(() => {
		if (
			issueState.difficulty !== null &&
			!difficulties.some((option) => option.level === issueState.difficulty)
		) {
			setIssueState((current) => ({
				...current,
				difficulty: null
			}))
		}
	}, [difficulties, issueState.difficulty])

	useEffect(() => {
		const down = (e: KeyboardEvent) => {
			if (["Alt", "Control", "Meta"].includes(e.key)) {
				modifierKeyDown.current = true
				return
			}

			const hasShortcutModifier =
				modifierKeyDown.current ||
				e.metaKey ||
				e.ctrlKey ||
				e.altKey ||
				e.getModifierState("Meta") ||
				e.getModifierState("Control") ||
				e.getModifierState("Alt")

			if (hasShortcutModifier) return

			if (
				open ||
				e.key.toLowerCase() !==
					firstShortcutKey(quickNavigationShortcuts.create_issue.keys) ||
				!enabled
			) {
				return
			}
			const activeElement = document.activeElement
			const isEditable =
				activeElement?.hasAttribute("contenteditable") ||
				activeElement?.tagName === "INPUT" ||
				activeElement?.tagName === "TEXTAREA"

			if (!isEditable) {
				e.preventDefault()
				setOpen(true)
			}
		}
		const up = (e: KeyboardEvent) => {
			if (["Alt", "Control", "Meta"].includes(e.key)) {
				modifierKeyDown.current = false
			}
		}
		const resetModifiers = () => {
			modifierKeyDown.current = false
		}
		const createIssue = () => {
			if (enabled) setOpen(true)
		}

		document.addEventListener("keydown", down)
		document.addEventListener("keyup", up)
		window.addEventListener("blur", resetModifiers)
		document.addEventListener("orbit:create-issue", createIssue)
		return () => {
			document.removeEventListener("keydown", down)
			document.removeEventListener("keyup", up)
			window.removeEventListener("blur", resetModifiers)
			document.removeEventListener("orbit:create-issue", createIssue)
		}
	}, [open, enabled])

	useEffect(() => {
		const down = (e: KeyboardEvent) => {
			if (!open || !isModifiedShortcut(e, appShortcuts.submit_issue.keys))
				return
			if (!canSubmitIssue) return

			e.preventDefault()
			submitIssue()
		}

		document.addEventListener("keydown", down)
		return () => document.removeEventListener("keydown", down)
	}, [canSubmitIssue, open, submitIssue])

	useEffect(() => {
		if (open) {
			setTeamId(presetTeamId ?? null)
			setIssueState((current) => ({
				...current,
				projectId: presetProjectId ?? null,
				status: presetValues?.status ?? IssueStatus.ToDo,
				priority: presetValues?.priority ?? IssuePriority.None,
				difficulty: presetValues?.difficulty ?? null,
				assigneeId: presetValues?.assigneeId ?? null
			}))
			refetchAllTeams()
		}
	}, [
		open,
		presetTeamId,
		presetProjectId,
		presetValues?.status,
		presetValues?.priority,
		presetValues?.difficulty,
		presetValues?.assigneeId,
		refetchAllTeams
	])

	useEffect(() => {
		if (allTeams && allTeams.length === 1 && !teamId && allTeams[0]) {
			setTeamId(allTeams[0].id)
		}
	}, [allTeams, teamId])

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>{children}</DialogTrigger>
			<DialogContent
				className="rounded-2xl bg-background shadow-2xl"
				size="xl"
				layout="scroll"
			>
				<DialogHeader variant="divided">
					<VisuallyHidden asChild>
						<DialogTitle>Create Issue</DialogTitle>
					</VisuallyHidden>
					<div className="flex w-full flex-row items-center justify-between gap-3">
						<div className="flex min-w-0 flex-row items-center gap-2">
							<IssueSetting
								options={
									allTeams?.map((team) => ({
										value: team.id,
										display: (
											<EntityIdentity
												kind="team"
												name={team.name}
												image={team.logo}
												size="sm"
											/>
										)
									})) ?? []
								}
								placeholder={
									<>
										<CircleHelpIcon className="size-4 text-icons" />
										<span>Team</span>
									</>
								}
								value={teamId}
								onChange={(nextTeamId) => {
									if (nextTeamId === teamId) return
									setTeamId(nextTeamId)
									setIssueState((current) => ({ ...current, projectId: null }))
								}}
								customNotFound="No teams found"
								disabled={createIssue.isPending}
								triggerClassName="w-auto"
							/>
							<TemplatePicker
								teamId={teamId ?? ""}
								kind={ContentTemplateKind.Issue}
								value={issueState.description}
								onApply={(description) =>
									setIssueState((current) => ({ ...current, description }))
								}
								disabled={!teamId || createIssue.isPending}
							/>
							<RefreshButton />
						</div>
						<DialogClose asChild>
							<Button
								variant="ghost"
								size="icon"
								className="text-icons"
								aria-label="Close new issue"
							>
								<XIcon />
							</Button>
						</DialogClose>
					</div>
				</DialogHeader>
				<div className="grid min-h-0 gap-0 overflow-hidden lg:grid-cols-[minmax(0,1fr)_280px]">
					<div className="flex min-h-0 flex-col gap-4 overflow-y-auto px-4 py-5 sm:px-7">
						<Input
							placeholder="Issue title"
							value={issueState.title}
							onChange={(e) => {
								setIssueState({ ...issueState, title: e.target.value })
							}}
							variant="title"
						/>
						<Editor
							teamId={teamId ?? undefined}
							disabled={createIssue.isPending}
							initialState={issueState.description}
							onChange={(editorState) => {
								setIssueState({
									...issueState,
									description: editorState
								})
							}}
						/>
					</div>
					<aside className="border-l border-stroke bg-background px-5 py-5">
						<p className="mb-3 text-sm font-semibold text-text-main">
							Properties
						</p>
						<div className="flex flex-col gap-2">
							<IssuePropertiesFields
								value={issueState}
								onChange={(change) =>
									setIssueState({ ...issueState, ...change })
								}
								disabled={createIssue.isPending}
								activeTeamId={teamId}
							/>

							<IssueSetting<string | null>
								options={issueSettingAssignees(teamData?.teamMembers ?? [])}
								placeholder={
									<>
										<UserCircleIcon className="size-4 text-icons" />
										<span>Assignee</span>
									</>
								}
								value={issueState.assigneeId}
								onChange={(assigneeId) => {
									setIssueState({
										...issueState,
										assigneeId
									})
								}}
								withSearch
								disabled={createIssue.isPending}
								customNotFound="No assignees found. Did you select a team in the top left?"
							/>
							{teamData?.enableProjects && teamData.id === teamId && (
								<IssueProjectPicker
									variant="flat"
									teamId={teamData.id}
									value={issueState.projectId}
									onChange={(projectId) =>
										setIssueState((current) => ({ ...current, projectId }))
									}
									disabled={createIssue.isPending}
								/>
							)}
							<IssueLabelPicker
								labels={
									teamLabels?.map((label) => ({
										value: `${label.id}¦${label.name}`, // needed because search is by value, and we want to search by name, not id
										data: label
									})) ?? []
								}
								selected={issueState.labelIds}
								onChange={(labelIds) => {
									setIssueState({
										...issueState,
										labelIds
									})
								}}
								withSearch
								disabled={createIssue.isPending}
							>
								<Button variant="picker" size="sm" className="w-full">
									<PlusIcon />
									Add labels
								</Button>
							</IssueLabelPicker>
							<IssueLabelShowcase
								labels={(teamLabels || []).filter((label) =>
									issueState.labelIds.includes(label.id)
								)}

								truncateNamesTo={30}
								onRemove={(labelId) => {
									setIssueState({
										...issueState,
										labelIds: issueState.labelIds.filter((id) => id !== labelId)
									})
								}}
							/>
						</div>
					</aside>
				</div>
				<DialogFooter variant="divided">
					<div className="flex w-full flex-row items-center justify-between gap-2">
						<Button size="sm" variant={"ghost"} disabled>
							<PaperclipIcon className="size-4 text-icons" />
						</Button>
						<Button
							size="sm"
							type="submit"
							disabled={
								!teamData?.id ||
								createIssue.isPending ||
								!issueState.title.trim() ||
								(!canQueue && !canCreate)
							}
							onClick={submitIssue}
							pending={createIssue.isPending}
						>
							Create Issue
						</Button>
					</div>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
